
<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import { FIN_ST_MOV_BANC } from '@/constants/FIN_ST_MOV_BANC'
import { formatarDataBR, formatarDataHoraBR } from '@/utils/useDataBR'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const showModalHelper = ref(false)

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

watch(showModalHelper, (aberto) => {
  if (aberto) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = z
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
})

onUnmounted(() => {
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['cancelar', 'show-notification'])

const metadataStore = useMetadataListasStore()

const isLoadingListas = ref(false)
const listas = reactive({})

const item = computed(() => props.itemParaEditar || {})

// --- Helpers de formatação ---
const formatarNumero = (valor, decimais = 2) => {
  if (valor == null || valor === '') return '-'
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: decimais,
    maximumFractionDigits: decimais
  }).format(Number(valor))
}

const formatarTaxa = (valor) => formatarNumero(valor, 4)

// --- Status ---
const obterTextoStatus = (status) => {
  if (status === FIN_ST_MOV_BANC.Criada) return 'Criada'
  if (status === FIN_ST_MOV_BANC.Baixada) return 'Baixada'
  if (status === FIN_ST_MOV_BANC.Conciliada) return 'Conciliada'
  if (status === FIN_ST_MOV_BANC.Estornada) return 'Estornada'
  return status || '-'
}

const isEstornada = computed(() => item.value.lstStatusMovBancaria === FIN_ST_MOV_BANC.Estornada)

// --- Resolução de listas lookup (case-insensitive) ---
const resolverTextoLista = (listaKey, id) => {
  if (!id) return '-'
  // Tenta a chave exata primeiro; se não achar, tenta com a primeira letra minúscula
  let lista = listas[listaKey]
  if (!lista) {
    const altKey = listaKey.charAt(0).toLowerCase() + listaKey.slice(1)
    lista = listas[altKey]
  }
  if (!lista) return id
  const item = lista.find(i =>
    String(i.idListaSistemaItem) === String(id) ||
    String(i.idListaClienteItem) === String(id)
  )
  return item ? item.textoLista : id
}

const textoMoeda = (id) => resolverTextoLista('LstMoeda', id)
const textoFormaPagamento = computed(() => resolverTextoLista('LstFormaPagamento', item.value.lstFormaPagamento))
const textoTipoMovimentacao = computed(() => resolverTextoLista('LstTipoMovimentacao', item.value.lstTipoMovimentacao))
const textoMovimento = computed(() => resolverTextoLista('LstMovimento', item.value.lstMovimento))

const textoMovimentoItem = (linha) => resolverTextoLista('LstMovimento', linha?.lstMovimento)
const textoMotivoEstorno = computed(() => resolverTextoLista('LstMotivoEstorno', item.value.lstMotivoEstorno))

const obterSiglaMoeda = (codigoMoeda) => {
  if (!codigoMoeda) return '-'
  const idStr = String(codigoMoeda)
  const itemLista = (listas.LstMoeda || []).find(m =>
    String(m.idListaSistemaItem) === idStr ||
    String(m.idListaClienteItem) === idStr
  )
  const texto = itemLista ? itemLista.textoLista : codigoMoeda
  const match = String(texto).match(/^[A-Z]{3}/)
  return match ? match[0] : codigoMoeda
}

const siglaMoedaBanco = computed(() => obterSiglaMoeda(item.value.lstMoedaBanco))
const siglaMoedaTitulo = computed(() => obterSiglaMoeda(item.value.lstMoedaTitulo))
const siglaMoedaPadrao = computed(() => obterSiglaMoeda(item.value.lstMoedaPadrao))
const siglaMoedaGerencial = computed(() => obterSiglaMoeda(item.value.lstMoedaGerencial))

const labelMoedaTituloPadrao = computed(() => {
  return `Taxa PTAX ${siglaMoedaTitulo.value}/${siglaMoedaPadrao.value}`
})

const labelMoedaTituloGerencial = computed(() => {
  return `Taxa Câmbio ${siglaMoedaTitulo.value}/${siglaMoedaGerencial.value}`
})

const labelMoedaBancoPadrao = computed(() => {
  return `Taxa PTAX ${siglaMoedaBanco.value}/${siglaMoedaPadrao.value}`
})

const labelMoedaBancoGerencial = computed(() => {
  return `Taxa Câmbio ${siglaMoedaBanco.value}/${siglaMoedaGerencial.value}`
})

const textoFonteCambioPadrao = computed(() => resolverTextoLista('LstFonteCambioPadrao', item.value.lstFonteCambioPadrao))
const textoStatusCambioPadrao = computed(() => resolverTextoLista('LstTaxaCambioStatusPadrao', item.value.lstTaxaCambioStatusPadrao))
const textoFonteCambioGerencial = computed(() => resolverTextoLista('LstFonteCambioGerencial', item.value.lstFonteCambioGerencial))
const textoStatusCambioGerencial = computed(() => resolverTextoLista('LstTaxaCambioStatusGerencial', item.value.lstTaxaCambioStatusGerencial))

// --- Compted dos totais dos Itens de Rateio ---
// --- Carregamento de listas ---
const carregarListas = async () => {
  isLoadingListas.value = true
  try {
    const result = await metadataStore.carregarListasParaTabelas(['MovimentacaoBancaria', 'MovimentacaoBancariaItem'])
    Object.assign(listas, result)

    if (!listas.LstFormaPagamento) {
      try {
        const res = await api.get('/ListasSistema/lookup/FIN_FORMAS_PG')
        listas.LstFormaPagamento = res.data
      } catch (e) { console.warn('FIN_FORMAS_PG não encontrada') }
    }

    if (!listas.LstMotivoEstorno) {
      try {
        const res = await api.get('/ListasCliente/lookup/FIN_MOTIV_ESTORNO')
        listas.LstMotivoEstorno = res.data
      } catch (e) { console.warn('FIN_MOTIV_ESTORNO não encontrada') }
    }

    // MOE_FONTE_COTACAO — resolução amigável de Fonte da Cotação
    if (!listas.LstFonteCambioPadrao && !listas.LstFonteCambioGerencial) {
      try {
        const res = await api.get('/ListasSistema/lookup/MOE_FONTE_COTACAO')
        listas.LstFonteCambioPadrao = res.data
        listas.LstFonteCambioGerencial = res.data
      } catch (e) { console.warn('MOE_FONTE_COTACAO não encontrada') }
    }

    // FIN_ST_TAX_CONVERCAO — resolução amigável de Status da Taxa
    if (!listas.LstTaxaCambioStatusPadrao && !listas.LstTaxaCambioStatusGerencial) {
      try {
        const res = await api.get('/ListasSistema/lookup/FIN_ST_TAX_CONVERCAO')
        listas.LstTaxaCambioStatusPadrao = res.data
        listas.LstTaxaCambioStatusGerencial = res.data
      } catch (e) { console.warn('FIN_ST_TAX_CONVERCAO não encontrada') }
    }
  } catch (error) {
    emit('show-notification', { message: 'Erro ao carregar dados auxiliares.', type: 'error' })
  } finally {
    isLoadingListas.value = false
  }
}

const handleFechar = () => {
  emit('cancelar')
}

onMounted(async () => {
  await carregarListas()
})
</script>

<template>
  <div class="movimentacao-bancaria-relatorio-wrapper">
    <!-- ============================================================ -->
    <!-- CABEÇALHO (Estilo FormularioCadastro)                       -->
    <!-- ============================================================ -->
    <header class="relatorio-header">
      <div class="titulo-wrapper">
        <h4 class="titulo-principal">Extrato Detalhado</h4>
        <span class="titulo-acao">Movimentação Bancária — Visualização de relatório</span>
      </div>

      <div class="info-registro">
        <div class="info-item">
          <strong>ID:</strong>
          <span class="info-valor">#{{ item.idMovimentacaoBancaria }}</span>
        </div>
        <div class="info-item">
          <strong>Movimentação:</strong>
          <span class="info-valor">{{ formatarNumero(item.valorEfetivoMoedaBanco) }} {{ siglaMoedaBanco }} — {{ formatarDataBR(item.dataMovimento) }}</span>
        </div>
        <div class="info-item">
          <strong>Status:</strong>
          <span class="info-valor">{{ obterTextoStatus(item.lstStatusMovBancaria) }}</span>
        </div>
      </div>

      <div class="botoes-wrapper">
        <button type="button" class="btn btn-secondary" @click="handleFechar">
          <i class="bi bi-x-lg me-1"></i> Fechar
        </button>
      </div>
    </header>

    <!-- ============================================================ -->
    <!-- CONTEÚDO                                                     -->
    <!-- ============================================================ -->
    <div class="relatorio-body">
      <div v-if="isLoadingListas" class="text-center p-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
      </div>

      <template v-else>
        <!-- ============================================================ -->
        <!-- LINHA RESUMO EXECUTIVO (Valor + Data)                        -->
        <!-- ============================================================ -->
        <div class="resumo-executivo-row mb-4">
          <div class="resumo-card">
            <span class="resumo-label">Valor da Operação</span>
            <span class="campo-valor destaque">{{ formatarNumero(item.valorEfetivoMoedaBanco) }} {{ siglaMoedaBanco }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Data do Movimento</span>
            <span class="campo-valor destaque">{{ formatarDataBR(item.dataMovimento) }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Tipo de Operação</span>
            <span class="campo-valor destaque">{{ textoTipoMovimentacao }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Movimento</span>
            <span class="campo-valor destaque" :class="item.lstMovimento === 'TP_MOVIMENTO1' ? 'text-success' : 'text-danger'">
              <i :class="item.lstMovimento === 'TP_MOVIMENTO1' ? 'bi bi-arrow-up-circle' : 'bi bi-arrow-down-circle'" class="me-1"></i>
              {{ textoMovimento }}
            </span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Forma de Pagamento</span>
            <span class="campo-valor destaque">{{ textoFormaPagamento }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Contrato de Câmbio</span>
            <span class="campo-valor destaque">{{ item.idContratoCambio ? `#${item.idContratoCambio}` : '—' }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Código Transação</span>
            <span class="campo-valor destaque">{{ item.codigoTransacaoBanco || '—' }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Data de Conciliação</span>
            <span class="campo-valor destaque">{{ formatarDataBR(item.dataConciliacao) || '—' }}</span>
          </div>
          <div class="resumo-card">
            <span class="resumo-label">Data de Estorno</span>
            <span class="campo-valor destaque">{{ formatarDataBR(item.dataEstorno) || '—' }}</span>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- BLOCO 1: REALIDADE BANCÁRIA                                  -->
        <!-- ============================================================ -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-bank me-2 text-primary"></i> Realidade Bancária</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-3">
                <div class="campo-exibicao">
                  <label class="campo-label">Banco</label>
                  <span class="campo-valor destaque">{{ item.nomeBanco || '-' }}</span>
                </div>
              </div>
              <div class="col-md-3">
                <div class="campo-exibicao">
                  <label class="campo-label">Nome da Conta</label>
                  <span class="campo-valor destaque">{{ item.nomeContaBancaria || '-' }}</span>
                </div>
              </div>
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Agência</label>
                  <span class="campo-valor destaque">{{ item.agenciaConta || '-' }}</span>
                </div>
              </div>
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Conta</label>
                  <span class="campo-valor destaque">{{ item.numeroConta || '-' }}</span>
                </div>
              </div>
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Moeda da Conta</label>
                  <span class="campo-valor destaque">{{ item.moedaConta ? textoMoeda(item.moedaConta) : '-' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- BLOCO 2: VALORES CONVERTIDOS (Banco > Padrão > Gerencial)     -->
        <!-- ============================================================ -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-currency-exchange me-2 text-primary"></i> Valores Convertidos</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-4">
                <div class="campo-exibicao">
                  <label class="campo-label">Valor na Moeda da Conta</label>
                  <span class="campo-valor destaque">{{ formatarNumero(item.valorEfetivoMoedaBanco) }} {{ siglaMoedaBanco }}</span>
                </div>
              </div>
              <div class="col-md-4">
                <div class="campo-exibicao">
                  <label class="campo-label">Valor na Moeda Padrão</label>
                  <span class="campo-valor destaque">{{ formatarNumero(item.valorEfetivoMoedaPadrao) }} {{ siglaMoedaPadrao }}</span>
                   <small class="campo-moeda">{{ labelMoedaBancoPadrao }}: {{ formatarTaxa(item.taxaBancoPadrao) }}</small>
                </div>
              </div>
              <div class="col-md-4">
                <div class="campo-exibicao">
                  <label class="campo-label">Valor na Moeda Gerencial</label>
                  <span class="campo-valor destaque">{{ formatarNumero(item.valorEfetivoMoedaGerencial) }} {{ siglaMoedaGerencial }}</span>
                  <small class="campo-moeda"> {{ labelMoedaBancoGerencial }}: {{ formatarTaxa(item.taxaBancoGerencial) }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- DIVISOR — Fim Região Bancária / Início Região do Título       -->
        <!-- ============================================================ -->
        <hr class="regiao-divider my-4" style="border-top: 3px dashed var(--bs-border-color); opacity: 0.6;">
        <div class="text-center mb-4">
          <span class="badge bg-secondary-subtle text-secondary-emphasis px-4 py-2 fs-6">
            <i class="bi bi-arrow-down-short me-2"></i>Abatimento da Dívida e Variações Cambiais
          </span>
        </div>

        <!-- ============================================================ -->
        <!-- BLOCO 4: TÍTULO FINANCEIRO                                   -->
        <!-- ============================================================ -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-file-text me-2 text-primary"></i> Título Financeiro</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Título Vinculado</label>
                  <span class="campo-valor destaque">
                    <i class="bi bi-link-45deg me-1 text-muted"></i>
                    {{ item.idTituloFinanceiro ? `#${item.idTituloFinanceiro}` : 'Avulsa / Tarifa' }}
                  </span>
                  <small v-if="item.nomeParceiroTitulo" class="campo-helper">{{ item.nomeParceiroTitulo }}</small>
                </div>
              </div>
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Dívida Original Total</label>
                  <span class="campo-valor destaque">{{ formatarNumero(item.valorOriginalTitulo) }} {{ siglaMoedaTitulo }}</span>
                </div>
              </div>
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Valor Abatido</label>
                  <span class="campo-valor destaque">{{ formatarNumero(item.valorAbatidoTitulo) }} {{ siglaMoedaTitulo }}</span>
                </div>
              </div>
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Valor Desconto</label>
                  <span class="campo-valor destaque">{{ formatarNumero(item.valorDescontoTitulo) }} <span v-if="Number(item.valorDescontoTitulo) > 0"> {{ siglaMoedaTitulo }}</span></span>
                </div>
              </div>
              <div class="col-md-2">
                <div class="campo-exibicao">
                  <label class="campo-label">Desconto Financeiro</label>
                  <span class="campo-valor destaque">{{ formatarNumero(item.valorDescontoFinanceiroTitulo) }} <span v-if="Number(item.valorDescontoFinanceiroTitulo) > 0"> {{ siglaMoedaTitulo }}</span></span>
                </div>
              </div>
              <div class="col-md-1">
                <div class="campo-exibicao">
                  <label class="campo-label">Taxa Base</label>
                  <span class="campo-valor destaque">{{ formatarTaxa(item.taxaCambioPadraoTitulo) }}</span>
                  <small class="campo-moeda">{{ siglaMoedaTitulo }}/{{ siglaMoedaPadrao }}</small>
                </div>
              </div>
              <div class="col-md-1">
                <div class="campo-exibicao">
                  <label class="campo-label">Taxa Gerencial</label>
                  <span class="campo-valor destaque">{{ formatarTaxa(item.taxaCambioGerencialTitulo) }}</span>
                  <small class="campo-moeda">{{ siglaMoedaTitulo }}/{{ siglaMoedaGerencial }}</small>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- BLOCO 5: TAXAS CAMBIAIS DUAL-LEDGER (2 COLUNAS)               -->
        <!-- ============================================================ -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-currency-exchange me-2 text-primary"></i> Taxas Cambiais Dual-Ledger</h5>
          </div>
          <div class="card-body">
            <div class="row g-4">
              <!-- COLUNA FISCAL (Moeda Base) -->
              <div class="col-md-6">
                <div class="dual-ledger-col borda-fiscal">
                  <div class="dual-ledger-header bg-fiscal">
                    <i class="bi bi-currency-dollar me-1"></i>Moeda Base — {{ siglaMoedaPadrao }}
                  </div>
                  <div class="dual-ledger-body">
                    <div class="dual-item">
                      <span class="dual-label">{{ labelMoedaTituloPadrao }}</span>
                      <span class="dual-valor taxa">{{ formatarTaxa(item.taxaTituloPadrao) }}</span>
                    </div>
                    <div class="dual-item">
                      <span class="dual-label">Data Referência</span>
                      <span class="dual-valor">{{ formatarDataBR(item.dataReferenciaCambioPadrao) }}</span>
                    </div>
                    <div class="dual-item">
                      <span class="dual-label">Fonte da Cotação</span>
                      <span class="dual-valor">{{ textoFonteCambioPadrao }}</span>
                    </div>
                    <div class="dual-item">
                      <span class="dual-label">Status da Taxa</span>
                      <span class="dual-valor">{{ textoStatusCambioPadrao }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- COLUNA GERENCIAL (Moeda Gerencial) -->
              <div class="col-md-6">
                <div class="dual-ledger-col borda-gerencial">
                  <div class="dual-ledger-header bg-gerencial">
                    <i class="bi bi-currency-exchange me-1"></i>Moeda Gerencial — {{ siglaMoedaGerencial }}
                  </div>
                  <div class="dual-ledger-body">
                    <div class="dual-item">
                      <span class="dual-label">{{ labelMoedaTituloGerencial }}</span>
                      <span class="dual-valor taxa">{{ formatarTaxa(item.taxaTituloGerencial) }}</span>
                    </div>
                    <div class="dual-item">
                      <span class="dual-label">Data Referência</span>
                      <span class="dual-valor">{{ formatarDataBR(item.dataReferenciaCambioGerencial) }}</span>
                    </div>
                    <div class="dual-item">
                      <span class="dual-label">Fonte da Cotação</span>
                      <span class="dual-valor">{{ textoFonteCambioGerencial }}</span>
                    </div>
                    <div class="dual-item">
                      <span class="dual-label">Status da Taxa</span>
                      <span class="dual-valor">{{ textoStatusCambioGerencial }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- BLOCO 6: CATEGORIZAÇÃO FINANCEIRA (MovimentacaoBancariaItem)  -->
        <!-- ============================================================ -->
        <div class="card mb-4">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h5 class="mb-0"><i class="bi bi-diagram-3 me-2 text-primary"></i> Categorização Financeira</h5>
            <button type="button" class="btn btn-sm btn-outline-secondary helper-btn" @click="showModalHelper = true" title="Guia de Leitura">
              <i class="bi bi-question-circle"></i>
            </button>
          </div>
          <div class="card-body p-0">
            <div class="admin-table-container border-0 shadow-none mb-0">
              <table class="table admin-table m-0">
                <thead>
                  <tr>
                    <th>Categoria Financeira</th>
                    <th>Descrição</th>
                    <th>Movimento</th>
                    <th class="text-end">Valor Base ({{ siglaMoedaPadrao }})</th>
                    <th class="text-end">Valor Gerencial ({{ siglaMoedaGerencial }})</th>
                    <th class="text-center" style="width:100px;">Origem</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!item.itens || item.itens.length === 0">
                    <td colspan="7" class="text-center text-muted py-4">
                      Nenhum item de rateio vinculado a esta movimentação.
                    </td>
                  </tr>
                  <tr v-for="(linha, index) in item.itens" :key="index">
                    <td class="fw-medium">
                      {{ linha.codigoEstruturado ? linha.codigoEstruturado + ' - ' + linha.nomeCategoriaFinanceira : linha.nomeCategoriaFinanceira || '#' + linha.idCategoriaFinanceira }}
                      <span v-if="linha.isAbatidoTitulo" class="badge bg-success-subtle text-success-emphasis ms-1 px-2 py-1" style="font-size:0.65rem; vertical-align:middle;" title="Este item abateu diretamente a dívida do título">
                        <i class="bi bi-check-circle me-1"></i>Abateu Título
                      </span>
                    </td>
                    <td><small>{{ linha.descricaoComplementar || '-' }}</small></td>
                    <td>{{ textoMovimentoItem(linha) }}</td>
                    <td class="text-end">{{ formatarNumero(linha.valorMoedaPadrao) }}</td>
                    <td class="text-end">{{ formatarNumero(linha.valorMoedaGerencial) }}</td>
                    <td class="text-center">
                      <span v-if="linha.isLancamentoManual" class="badge bg-warning-subtle text-warning-emphasis px-2 py-1" title="Lançamento Manual">
                        <i class="bi bi-pencil-fill me-1"></i>Manual
                      </span>
                      <span v-else class="badge bg-secondary-subtle text-secondary-emphasis px-2 py-1" title="Automático (do título)">
                        <i class="bi bi-gear-fill me-1"></i>Auto
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- ============================================================ -->
        <!-- BLOCO 7: AUDITORIA E RASTREABILIDADE                         -->
        <!-- ============================================================ -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0"><i class="bi bi-shield-check me-2 text-primary"></i> Auditoria e Rastreabilidade</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-3">
                <div class="campo-exibicao">
                  <label class="campo-label">Criado em</label>
                  <span class="campo-valor">{{ formatarDataHoraBR(item.tsCriacao) }}</span>
                </div>
              </div>
              <div class="col-md-3">
                <div class="campo-exibicao">
                  <label class="campo-label">Última Modificação</label>
                  <span class="campo-valor">{{ formatarDataHoraBR(item.tsModificacao) }}</span>
                </div>
              </div>
            </div>

            <!-- Bloco de Estorno (condicional) -->
            <div v-if="isEstornada" class="estorno-box mt-3 p-3 rounded-3">
              <div class="d-flex align-items-center mb-2">
                <i class="bi bi-arrow-counterclockwise me-2 text-danger fs-5"></i>
                <strong class="text-danger">Dados do Estorno</strong>
              </div>
              <div class="row g-3">
                <div class="col-md-4">
                  <div class="campo-exibicao">
                    <label class="campo-label">Motivo do Estorno</label>
                    <span class="campo-valor">{{ textoMotivoEstorno }}</span>
                  </div>
                </div>
                <div class="col-md-8">
                  <div class="campo-exibicao">
                    <label class="campo-label">Justificativa</label>
                    <span class="campo-valor texto-livre">{{ item.justificativaEstorno || '—' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Observação, se houver -->
            <div v-if="item.observacao" class="row g-3 mt-2">
              <div class="col-md-12">
                <div class="campo-exibicao">
                  <label class="campo-label">Observações</label>
                  <span class="campo-valor texto-livre">{{ item.observacao }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- ============================================================ -->
  <!-- MODAL HELPER — Guia de Leitura                               -->
  <!-- ============================================================ -->
  <Teleport to="body">
    <div v-if="showModalHelper" class="modal-backdrop fade show" :style="{ zIndex: zIndexEfetivo }" @click="showModalHelper = false"></div>
    <div v-if="showModalHelper" class="modal fade show d-block" :style="{ zIndex: zIndexEfetivo + 1 }" tabindex="-1" role="dialog" @click.self="showModalHelper = false">
      <div class="modal-dialog modal-lg modal-dialog-scrollable" role="document">
        <div class="modal-content helper-modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-book me-2 text-primary"></i>
              Guia de Leitura: Auditoria de Baixa Bancária e Câmbio
            </h5>
            <button type="button" class="btn-close" @click="showModalHelper = false" aria-label="Fechar"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted mb-4">
              Esta tela apresenta a conciliação completa da sua movimentação, separando o <strong>fluxo de caixa real</strong> (o que movimentou no banco) dos <strong>ajustes contábeis de resultado</strong> (DRE), operando simultaneamente na sua moeda local e na moeda de reporte gerencial.
            </p>
            <p class="text-muted mb-4">
              Para auditar os valores, siga as regras abaixo:
            </p>

            <div class="helper-section">
              <h6 class="helper-section-title">
                <span class="helper-badge">1</span>
                A Regra do Fechamento do Caixa
              </h6>
              <p>Para encontrar o valor exato que consta no seu Extrato Bancário, você deve observar <strong>apenas</strong> as linhas de fluxo financeiro na grid de Categorização:</p>
              <ul>
                <li><strong>(+) ou (-)</strong> Valor do Principal / FOB.</li>
                <li><strong>(+) ou (-)</strong> Juros, Multas e Descontos aplicados.</li>
                <li><strong>(+) ou (-)</strong> Ganhos ou Perdas com Spread Bancário.</li>
              </ul>
              <div class="helper-callout">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                <span><strong>Atenção:</strong> A linha de <strong>Custo/Receita com Variação Cambial</strong> é um ajuste puramente contábil. Ela <strong>não entra</strong> na soma para fechamento do extrato bancário.</span>
              </div>
            </div>

            <div class="helper-section">
              <h6 class="helper-section-title">
                <span class="helper-badge">2</span>
                Como ler o Spread Bancário (Eficiência da Operação)
              </h6>
              <p>O Spread mostra se a conversão do banco foi vantajosa ou prejudicial no momento exato do pagamento. O sistema calcula automaticamente o <strong>Valor Justo</strong> (Dívida + Juros - Descontos) e compara com o que efetivamente transitou no banco.</p>
              <ul>
                <li><strong>É classificado como Entrada (Ganho):</strong> Quando você recebeu mais reais do que a dívida valia, ou quando pagou menos reais do que deveria.</li>
                <li><strong>É classificado como Saída (Perda):</strong> Quando você recebeu menos reais do que a dívida valia, ou quando pagou mais reais do que deveria.</li>
              </ul>
            </div>

            <div class="helper-section">
              <h6 class="helper-section-title">
                <span class="helper-badge">3</span>
                Como ler a Variação Cambial (Ajuste de DRE)
              </h6>
              <p>Esta linha não é dinheiro novo entrando ou saindo do banco hoje. Ela mede a diferença histórica de valor.</p>
              <ul>
                <li>Ela compara quanto a sua dívida valia no <strong>dia da emissão</strong> do título contra o quanto ela passou a valer no <strong>dia da baixa</strong>.</li>
                <li>É o ajuste necessário para o Kardex/Estoque e para o balanço contábil refletirem o ganho ou perda de poder de compra gerado pelo tempo em que o título ficou aberto.</li>
              </ul>
            </div>

            <div class="helper-section">
              <h6 class="helper-section-title">
                <span class="helper-badge">4</span>
                A Visão Gerencial (Dual-Ledger)
              </h6>
              <p>A coluna <strong>Valor Gerencial (USD)</strong> traduz o poder de compra exato da operação para a matriz no dia do movimento.</p>
              <ul>
                <li>Para proteger o balanço global contra distorções irreais, se a sua operação ocorreu em uma moeda que não sofreu variação real frente à dívida (ex: Título em USD recebido em USD), o sistema forçará a Variação Cambial Gerencial a ser <strong>zero</strong>, mantendo o balanço global intacto.</li>
              </ul>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showModalHelper = false">
              <i class="bi bi-x-lg me-1"></i> Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
/* ============================================================ */
/* LAYOUT GERAL                                                 */
/* ============================================================ */
.movimentacao-bancaria-relatorio-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: var(--bs-body-bg);
}

.relatorio-body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* ============================================================ */
/* CABEÇALHO (Estilo FormularioCadastro)                        */
/* ============================================================ */
.relatorio-header {
  display: flex;
  height: 100px;
  align-items: center;
  gap: 60px;
  padding: 1.5rem;
  background-color: #34495E;
  border-bottom: 1px solid #1f2d3a;
  flex-shrink: 0;
}

.titulo-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.titulo-principal {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1.1;
  color: #FFFFFF;
}

.titulo-acao {
  font-size: 0.875rem;
  color: #ADB5BD;
}

.info-registro {
  font-size: 0.9rem;
  line-height: 1.8;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.info-item strong {
  color: #ADB5BD;
  margin-right: 0.25rem;
}

.info-valor {
  color: #ca8019;
  font-weight: 600;
}

.botoes-wrapper {
  margin-left: auto;
  flex-shrink: 0;
}

.botoes-wrapper .btn {
  color: #fff;
  border-color: rgba(255,255,255,0.3);
}

.botoes-wrapper .btn:hover {
  background-color: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.5);
}

/* ============================================================ */
/* RESUMO EXECUTIVO                                              */
/* ============================================================ */
.resumo-executivo-row {
  display: flex;
  gap: 1rem;
}

.resumo-card {
  flex: 1;
  background-color: var(--bs-tertiary-bg);
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  transition: box-shadow 0.2s ease;
}

.resumo-card:hover {
  box-shadow: 0 0.125rem 0.5rem rgba(0,0,0,0.08);
}

.resumo-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--bs-secondary-color);
  font-weight: 600;
}

/* ============================================================ */
/* CAMPOS DE EXIBIÇÃO                                           */
/* ============================================================ */
.campo-exibicao {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.campo-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color);
  font-weight: 600;
}

.campo-valor {
  font-size: 0.95rem;
  color: var(--bs-body-emphasis);
  font-weight: 500;
  word-break: break-word;
}

.campo-valor.destaque {
  font-size: 1.1rem;
  font-weight: 700;
}

.campo-valor.taxa {
  font-family: 'Courier New', Courier, monospace;
  font-weight: 600;
}

.campo-valor.mono {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.85rem;
}

.campo-valor.texto-livre {
  white-space: pre-wrap;
  font-weight: 400;
  line-height: 1.5;
}

.campo-moeda {
  font-size: 0.7rem;
  color: var(--bs-secondary-color);
  text-transform: uppercase;
  font-weight: 600;
}

.campo-helper {
  font-size: 0.75rem;
  color: var(--bs-secondary-color);
  margin-top: 0.1rem;
}

/* ============================================================ */
/* DUAL-LEDGER                                                   */
/* ============================================================ */
.dual-ledger-col {
  border: 1px solid var(--bs-border-color-translucent);
  border-radius: 0.5rem;
  overflow: hidden;
  height: 100%;
}

.borda-fiscal {
  border-color: #0d6efd;
}

.borda-gerencial {
  border-color: #198754;
}

.dual-ledger-header {
  padding: 0.65rem 1rem;
  font-weight: 700;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #fff;
}

.bg-fiscal {
  background-color: #0d6efd;
}

.bg-gerencial {
  background-color: #198754;
}

.dual-ledger-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.dual-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
}

.dual-label {
  font-size: 0.8rem;
  color: var(--bs-secondary-color);
  font-weight: 500;
  flex-shrink: 0;
}

.dual-valor {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--bs-body-emphasis);
  text-align: right;
}

.dual-valor.taxa {
  font-family: 'Courier New', Courier, monospace;
}

.dual-divider {
  margin: 0.3rem 0;
  border-color: var(--bs-border-color-translucent);
  opacity: 0.5;
}

/* ============================================================ */
/* ESTORNO BOX                                                   */
/* ============================================================ */
.estorno-box {
  background-color: rgba(220, 53, 69, 0.06);
  border: 1px solid rgba(220, 53, 69, 0.2);
}

/* ============================================================ */
/* CARDS                                                         */
/* ============================================================ */
.card {
  background-color: var(--bs-tertiary-bg);
  border-radius: 0.375rem;
  border: 1px solid var(--bs-border-color-translucent) !important;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.03);
}

.card-header {
  background-color: rgba(52, 73, 94, 0.06);
  border-bottom: 1px solid var(--bs-border-color-translucent) !important;
  padding: 0.85rem 1.25rem;
}

.card-header h5 {
  color: var(--bs-heading-color);
  font-weight: 600;
  font-size: 1.1rem;
}

.card-body {
  padding: 1.25rem;
}

/* ============================================================ */
/* BADGES (replicadas do sistema)                                */
/* ============================================================ */
:deep(.badge) {
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.65rem;
}

:deep(.badge-ghost-success) {
  background-color: rgba(25, 135, 84, 0.12);
  color: #198754;
  border: 1px solid rgba(25, 135, 84, 0.2);
}

:deep(.badge-ghost-info) {
  background-color: rgba(13, 202, 240, 0.12);
  color: #0dcaf0;
  border: 1px solid rgba(13, 202, 240, 0.2);
}

:deep(.badge-ghost-warning) {
  background-color: rgba(255, 193, 7, 0.12);
  color: #ffc107;
  border: 1px solid rgba(255, 193, 7, 0.2);
}

:deep(.badge-ghost-danger) {
  background-color: rgba(220, 53, 69, 0.12);
  color: #dc3545;
  border: 1px solid rgba(220, 53, 69, 0.2);
}

:deep(.badge-ghost-secondary) {
  background-color: rgba(108, 117, 125, 0.12);
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

/* ============================================================ */
/* DARK MODE AJUSTES                                             */
/* ============================================================ */
[data-bs-theme="dark"] .card-header {
  background-color: rgba(0, 0, 0, 0.15);
}

[data-bs-theme="dark"] .estorno-box {
  background-color: rgba(220, 53, 69, 0.1);
}

/* ============================================================ */
/* RESPONSIVIDADE                                                */
/* ============================================================ */
@media (max-width: 991.98px) {
  .relatorio-header {
    position: static;
    height: auto;
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .titulo-wrapper {
    align-items: center;
    text-align: center;
  }

  .info-registro {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.1rem;
    text-align: center;
  }

  .botoes-wrapper {
    margin-left: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  .botoes-wrapper .btn {
    width: 100%;
  }

  .resumo-executivo-row {
    flex-direction: column;
  }

  .dual-ledger-col {
    height: auto;
  }
}

/* ============================================================ */
/* HELPER MODAL                                                  */
/* ============================================================ */
.helper-modal-content {
  border: none;
  border-radius: 0.5rem;
}

.helper-modal-content .modal-header {
  background-color: #34495E;
  color: #fff;
  border-bottom: 1px solid #1f2d3a;
  padding: 1rem 1.25rem;
}

.helper-modal-content .modal-header .modal-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.helper-modal-content .modal-header .btn-close {
  filter: brightness(0) invert(1);
}

.helper-modal-content .modal-body {
  padding: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--bs-body-color);
}

.helper-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid var(--bs-border-color-translucent);
}

.helper-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.helper-section-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--bs-heading-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.helper-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background-color: #34495E;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  flex-shrink: 0;
}

.helper-section p {
  margin-bottom: 0.5rem;
}

.helper-section ul {
  margin-bottom: 0.5rem;
  padding-left: 1.25rem;
}

.helper-section ul li {
  margin-bottom: 0.25rem;
}

.helper-callout {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.2);
  border-radius: 0.375rem;
  font-size: 0.85rem;
  color: var(--bs-body-color);
  margin-top: 0.5rem;
}

.helper-callout i {
  color: #ffc107;
  font-size: 1rem;
  margin-top: 0.1rem;
  flex-shrink: 0;
}

.helper-btn {
  border-color: var(--bs-border-color);
  color: var(--bs-body-color);
  padding: 0.15rem 0.45rem;
  font-size: 0.85rem;
  line-height: 1.2;
  border-radius: 0.25rem;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  background-color: transparent;
}

.helper-btn:hover {
  background-color: var(--bs-tertiary-bg);
  border-color: var(--bs-secondary-color);
  color: var(--bs-body-color);
}

.helper-btn i {
  font-size: 1rem;
}
</style>
